import React from 'react';
import PropTypes from 'prop-types';
import {
  ERROR_TEXT_NO_DEFAULT_VALUE,
  ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL,
  ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS,
} from 'langs/en/errors';
import { convertMultipicklistValueToKey } from 'utils/idUtils';
import Table from './Multipicklist.Table';
import InputContainer from '../../../InputContainer';
import SelectAttribute from '../../../SelectAttribute';
import { FLAGS_NONREMOVABLE } from '../../../../utils/constants';

class FieldMultipicklist extends React.Component {

  validateAndChangeBulkShouldDisplay = (toggleEligibleOptions, value) => {
    this.props.bulkToggleDropdownOptions(this.props.values.id, toggleEligibleOptions, value);
    if (!value && toggleEligibleOptions.length === this.props.values.editors.multipicklistConfig.optionsOrder.length) {
      this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options'], false, ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS);
    } else {
      this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options'], true);
    }
  }

  validateAndChangeShouldDisplay = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    this.props.onFieldUIStateChange(keyPath, { touched: true });
    const { editors: { multipicklistConfig } } = this.props.values;
    const optionId = keyPath[keyPath.length - 2];
    const { defaultOption } = multipicklistConfig;
    const changedOptionValue = multipicklistConfig.options[optionId].value;

    if (defaultOption === changedOptionValue) {
      this.props.onFieldValueChange([...this.props.keyPath, 'multipicklistConfig', 'defaultOption'], '');
    }

    const optionsToDisplay = multipicklistConfig.optionsOrder.filter((optId) => {
      if (optionId === optId) return value;
      return multipicklistConfig.options[optId].shouldDisplay;
    });

    if (optionsToDisplay.length < 1) {
      this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options'], false, ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS);
    } else {
      this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options'], true);
    }

    if (multipicklistConfig.options[optionId].label === '') {
      if (value) {
        this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options', optionId, 'label'], false, ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL);
      } else {
        this.props.fieldValidityChanged([...this.props.keyPath, 'multipicklistConfig', 'options', optionId, 'label'], true);
      }
    }
  };

  validateAndChangeDefaultOption = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    if (this.props.values.hidden && value.length === 0) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_DEFAULT_VALUE);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeOptionLabel = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (value === '') {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  createSelectOptionsObject = (optionsObject) => {
    const optionsKeys = Object.keys(optionsObject);
    const selectOptions = {};
    optionsKeys.forEach((key) => {
      const current = optionsObject[key];
      if (current.shouldDisplay) {
        selectOptions[[current.value]] = current.label;
      }
    });
    return selectOptions;
  }

  render() {
    const { editors: { multipicklistConfig } } = this.props.values;
    const { graph } = this.props.fieldConditionalityInfo;
    let toggleableOptionIds = [...multipicklistConfig.optionsOrder];
    const userBranchingVerticies = graph.getVerticiesForUserBranchingField(this.props.values.id);
    if (userBranchingVerticies) {
      const orphanVerticies = graph.getOrphanedUserBranches();
      toggleableOptionIds = orphanVerticies
        .filter((vertex) => userBranchingVerticies[vertex])
        .map((vertex) => userBranchingVerticies[vertex])
        .map(convertMultipicklistValueToKey);
    }

    const selectOptionsMap = this.createSelectOptionsObject(multipicklistConfig.options);
    const standardFieldNameIsNull = !this.props.values.name;
    const requiredStandardField = this.props.values.flags && this.props.values.flags.includes(FLAGS_NONREMOVABLE);

    return (
      <div className="col mt-2">
        <div className="row mb-4">
          <div className="col-sm-6">
            <InputContainer
              label="Default Value"
              keyPath={[...this.props.keyPath, 'multipicklistConfig', 'defaultOption']}
              uiState={this.props.uiState.editors.multipicklistConfig.defaultOption}
              disabled={standardFieldNameIsNull || requiredStandardField}
            >
              <SelectAttribute
                keyPath={[...this.props.keyPath, 'multipicklistConfig', 'defaultOption']}
                value={multipicklistConfig.defaultOption}
                autofocus={standardFieldNameIsNull}
                options={selectOptionsMap}
                onFieldValueChange={this.validateAndChangeDefaultOption}
                onFieldUIStateChange={this.props.onFieldUIStateChange}
              />
            </InputContainer>
          </div>
        </div>
        <div className="row">
          <Table
            id={this.props.values.id}
            keyPath={[...this.props.keyPath, 'multipicklistConfig']}
            toggleableOptionIds={toggleableOptionIds}
            onChangeShouldDisplay={this.validateAndChangeShouldDisplay}
            onChangeOptionLabel={this.validateAndChangeOptionLabel}
            onFieldUIStateChange={this.props.onFieldUIStateChange}
            onReorderMultipicklist={this.props.reorderDropdown}
            onToggleBulkShouldDisplay={(_, value) => this.validateAndChangeBulkShouldDisplay(toggleableOptionIds, value)}
            uiState={this.props.uiState.editors.multipicklistConfig}
            values={multipicklistConfig}
          />
        </div>
      </div>
    );
  }
}

function FieldMultipicklistConfigure(props) {
  return <FieldMultipicklist {...props} />;
}

FieldMultipicklist.propTypes = {
  bulkToggleDropdownOptions: PropTypes.func.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  fieldConditionalityInfo: PropTypes.object.isRequired,
  values: PropTypes.shape({
    editors: PropTypes.shape({
      multipicklistConfig: PropTypes.shape({
        defaultOption: PropTypes.string.isRequired,
        options: PropTypes.object.isRequired,
        optionsOrder: PropTypes.array.isRequired,
      }).isRequired,
    }).isRequired,
    flags: PropTypes.array.isRequired,
    hidden: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    type: PropTypes.string.isRequired,
    uiOrder: PropTypes.number,
    __typename: PropTypes.string,
  }).isRequired,
  uiState: PropTypes.object.isRequired,
  keyPath: PropTypes.array.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  reorderDropdown: PropTypes.func.isRequired,
};

export default FieldMultipicklistConfigure;
